import { TOTALS_CELL_BG, TOTALS_ROW_CLASS } from 'components/TableTotals/styles'
import { getPrice2decimal, Num } from 'utils'

/**
 * Shared totals row for the app's money grids, so every grid totals the same way and looks the
 * same doing it. Raw `<table>` grids render `<TotalsRow>` directly; grids built on `PlainTable`
 * pass its `footer` prop, which uses the same classes.
 */

export { TOTALS_CELL_BG, TOTALS_ROW_CLASS }

/**
 * Sum of a currency column, tolerant of the comma-formatted strings the API returns and of
 * nulls/blanks. Rounded to cents so a long column cannot drift on floating point.
 */
export const sumBy = <T,>(rows: T[], value: keyof T | ((row: T) => any)): number => {
  const get = typeof value === 'function' ? value : (row: T) => (row as any)[value]
  return Number(rows.reduce((acc: number, row) => acc + Num(get(row)), 0).toFixed(2))
}

/** Money as it appears in a totals cell: right-aligned "$1,234.56". */
export const totalsMoney = (value: number | string) => (
  <div className="text-right tabular-nums">{getPrice2decimal(value, false, true, true)}</div>
)

/**
 * The same figure without the right alignment, for a grid whose money column is left-aligned -
 * a totals cell has to sit under the numbers it sums, not opposite them. Always two decimals,
 * even where the body rounds, because a total that hides cents is a total nobody can tie out.
 */
export const totalsMoneyPlain = (value: number | string) => getPrice2decimal(value, false, true, true)

/**
 * The label cell, with the number of rows the figures cover. Use it wherever the grid shows less
 * than the whole set - a capped fetch or a page - so nobody reads the total as covering more than
 * it does.
 */
export const totalsLabel = (count: number, noun = 'row') => (
  <div className="whitespace-nowrap">
    Totals
    <span className="ml-1 font-normal text-[11px] text-gray-500">
      ({count} {noun}
      {count === 1 ? '' : 's'})
    </span>
  </div>
)

/**
 * One cell of a totals row. A bare number is formatted as currency; a string or element is
 * rendered as given, which is how the label cell and any blank columns are passed.
 */
export type TotalsCell =
  | number
  | string
  | JSX.Element
  | null
  | undefined
  | {
      value?: number | string | JSX.Element | null
      /** Padding/alignment for this cell, used INSTEAD of `tdClass` - two Tailwind padding
       *  utilities on one element conflict, and which wins is stylesheet order, not source order. */
      className?: string
      colSpan?: number
    }

export const TotalsRow = ({
  cells,
  sticky = false,
  tdClass = 'px-6 py-3',
}: {
  /** One entry per column, in column order. Numbers are formatted as currency. */
  cells: TotalsCell[]
  /** Pin the row to the bottom of the scroll container, for a grid inside a `max-h-*` box. */
  sticky?: boolean
  /** Cell padding, to match the body of the grid it belongs to. */
  tdClass?: string
}) => (
  <tfoot className={TOTALS_ROW_CLASS}>
    <tr>
      {cells.map((cell, index) => {
        const spec =
          cell !== null && typeof cell === 'object' && 'value' in (cell as any) ? (cell as any) : { value: cell }
        const content = typeof spec.value === 'number' ? totalsMoney(spec.value) : spec.value ?? ''
        return (
          <td
            className={`${spec.className || tdClass} ${TOTALS_CELL_BG} ${sticky ? 'sticky bottom-0 z-20' : ''}`}
            colSpan={spec.colSpan}
            key={index}
          >
            {content}
          </td>
        )
      })}
    </tr>
  </tfoot>
)
